import styled, { css } from 'styled-components';
import { tokens } from '@/styles/tokens';

/* FR-006: the sequence of delay values that opens the hero (0/120/240/360ms) and through that
   the sequencing of delay values that each band of the landing page applies to its own voices when
   the observer reports the band in view. */
const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const StyledSectionSummary = styled.section<{ $background: 'default' | 'muted' }>`
  padding-block: ${tokens.space[16]};
  /* Full viewport height with the content centered, matching the Section primitive */
  /* specs/023: a snap target. Inert everywhere except the landing page, which is the only place this
     component renders, so no route check is needed. scroll-padding-top on <html> keeps the band below
     the header; a scroll-margin-top here would apply that offset a second time. */
  scroll-snap-align: start;
  min-height: 100vh; /* fallback for browsers without dynamic viewport units */
  min-height: 100dvh;
  display: grid;
  align-content: center;
  background-color: ${({ $background }) =>
    $background === 'muted' ? tokens.colors.bgMuted : tokens.colors.bg};

  @media (max-width: 767px) {
    padding-block: ${tokens.space[12]};
  }
`;

export const StyledSectionSummaryContent = styled.div`
  max-width: ${tokens.containers.xl};
  /* Two properties, and both are load-bearing for the technology marquee in the featured slot,
     whose track is width:max-content at nearly 7000px:

     width:100% because auto margins on a grid item switch off justify-self:stretch, so without
     an explicit width this box took its max-content width instead of the track's.

     min-width:0 because a grid item's automatic minimum size is its min-content width, which
     raised the track's own minimum to 1072px and pushed it past a 375px phone. */
  width: 100%;
  min-width: 0;
  margin-inline: auto;
  padding-inline: ${tokens.space[6]};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${tokens.space[6]};
`;

export const StyledSectionSummaryTitle = styled.h2<{ $visible: boolean }>`
  font-size: ${tokens.fontSizes['2xl']};
  font-weight: ${tokens.fontWeights.bold};
  color: ${tokens.colors.textPrimary};
  line-height: ${tokens.lineHeights.display};
  margin: 0;

  /* First voice, the hero's own opening: the title fades in with no delay, and the 'both' fill
     mode holds the from-state while the delay runs. */
  ${({ $visible }) =>
    $visible &&
    css`
      animation: fadeIn var(--duration-slow) var(--ease-out) both;
      animation-delay: 0ms;
    `}
  ${noMotion}
`;

export const StyledSectionSummaryDescription = styled.p<{ $visible: boolean }>`
  font-size: ${tokens.fontSizes.lg};
  color: ${tokens.colors.textSecondary};
  line-height: ${tokens.lineHeights.relaxed};
  max-width: 640px;
  margin: 0;

  /* Second voice: the description rises into place, as the hero's tagline does below the band. */
  ${({ $visible }) =>
    $visible &&
    css`
      animation: fadeInUp var(--duration-slow) var(--ease-out) both;
      animation-delay: 120ms;
    `}
  ${noMotion}
`;

export const StyledSectionSummaryCta = styled.a<{ $visible: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: ${tokens.space[2]} ${tokens.space[4]};
  margin-top: ${tokens.space[2]};
  font-family: ${tokens.fonts.sans};
  font-size: ${tokens.fontSizes.base};
  font-weight: ${tokens.fontWeights.medium};
  text-decoration: none;
  white-space: nowrap;
  border-radius: ${tokens.radii.full};
  background-color: ${tokens.colors.primary};
  color: ${tokens.colors.white};
  transition: background-color 120ms ease-out;

  &:hover {
    background-color: ${tokens.colors.primaryHover};
  }

  &:focus-visible {
    outline: 2px solid ${tokens.colors.focus};
    outline-offset: 2px;
  }

  /* Third voice, the hero's own button entrance. Its hover is background-only, so the scale(1)
     that the 'both' fill mode leaves behind cannot smother anything. */
  ${({ $visible }) =>
    $visible &&
    css`
      animation: scaleIn var(--duration-slow) var(--ease-out) both;
      animation-delay: 240ms;
    `}
  ${noMotion}
`;

export const StyledSectionSummaryFeatured = styled.div`
  margin-top: ${tokens.space[8]};
  width: 100%;
  max-width: ${tokens.containers.lg};
  /* A block's minimum content size is min(max-content, max-width), so max-width:1024px on its
     own puts a 1024px floor under this box. With a wide child such as the technology marquee,
     whose track is width:max-content at nearly 7000px, that floor exceeded a 375px phone and
     pushed a horizontal scrollbar onto the whole page. Zeroing the automatic minimum lets this
     box shrink to the space it actually has. */
  min-width: 0;
`;
